import styled from '@emotion/styled';
import { useMemberEdit } from './useMemberEdit';
import { Controller } from 'react-hook-form';
import { GENDER, USER_ROLE_LABEL_FOR_EDIT } from 'domain/entities/BuyerUser/BuyerUser';
import { FormInput } from 'ui/components/elements/FormInput/FormInput';
import { FormSelect } from 'ui/components/elements/FormSelect/FormSelect';
import { GroupedToggleFormSelect } from 'ui/components/elements/FormSelect/GroupedToggleFormSelect';
import { FormError } from 'ui/components/elements/FormError/FormError';
import { FormLabel } from 'ui/components/elements/FormLabel/FormLabel';
import { PageTitle } from 'ui/components/elements/PageTitle/PageTitle';
import { ActionButton } from 'ui/components/elements/ActionButton/ActionButton';
import { ImageUpload } from 'ui/components/features/ImageUpload/ImageUpload';
import { SnackBar } from 'ui/components/elements/SnackBar/SnackBar';
import { mqUser } from 'utils/responsive';

const Wrapper = styled.div`
    padding: 18px 50px;
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    text-align: left;
    background-color: #f8f9fd;
    height: auto;
    min-height: calc(100vh - var(--height-pc-header));

    ${mqUser('SP')} {
        padding: 18px 15px;
    }
`;

const FormContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px 40px 40px 40px;
    max-width: 700px;
    margin: 0 auto;
    width: 100%;
`;

const ButtonContainer = styled.div`
    margin: 0 auto;
    padding-bottom: 40px;
`;

const Birthday = styled.div`
    display: flex;
    width: 70%;
    > * {
        flex: 1;
    }

    @media (max-width: 768px) {
        width: 100%;
        > * {
            max-width: 33%;
        }
    }
`;

const FlexContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const Description = styled.div`
    font-size: 13px;
    font-weight: var(--font-bold);
    color: #626262;
    text-align: left;
`;

export const MemberEdit: React.FC = () => {
    const {
        showSnackbar,
        setShowSnackbar,
        control,
        errors,
        yearOptions,
        monthOptions,
        dayOptions,
        loading,
        submit,
        watch,
        setValue,
    } = useMemberEdit();
    return (
        <Wrapper>
            <SnackBar message="保存しました" open={showSnackbar} setOpen={setShowSnackbar} />
            <PageTitle title="メンバー編集" />
            <FormContainer>
                <FlexContainer>
                    <FormLabel title="お名前（姓）" isRequired />
                    <Description>※お名前はマッチングした企業様に公開されます。</Description>
                    <Controller
                        name="last_name"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} hasError={!!errors.last_name?.message} />
                                {errors.last_name?.message && <FormError message={errors.last_name.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="お名前（名）" isRequired />
                    <Controller
                        name="first_name"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} hasError={!!errors.first_name?.message} />
                                {errors.first_name?.message && <FormError message={errors.first_name.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="ふりがな（姓）" isRequired />
                    <Controller
                        name="last_name_kana"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} hasError={!!errors.last_name_kana?.message} />
                                {errors.last_name_kana?.message && (
                                    <FormError message={errors.last_name_kana.message} />
                                )}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="ふりがな（名）" isRequired />
                    <Controller
                        name="first_name_kana"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} hasError={!!errors.first_name_kana?.message} />
                                {errors.first_name_kana?.message && (
                                    <FormError message={errors.first_name_kana.message} />
                                )}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="性別" isRequired />
                    <Controller
                        name="gender"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormSelect
                                    {...field}
                                    options={Object.entries(GENDER).map(([key, value]) => ({
                                        value: key,
                                        label: value,
                                    }))}
                                />
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="電話番号" />
                    <Description>※ハイフンなしで入力してください。</Description>
                    <Controller
                        name="phone_number"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput
                                    {...field}
                                    value={field.value ?? ''}
                                    hasError={!!errors.phone_number?.message}
                                />
                                {errors.phone_number?.message && <FormError message={errors.phone_number.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="生年月日" />
                    <Birthday>
                        <Controller
                            name="year"
                            control={control}
                            render={({ field }) => (
                                <GroupedToggleFormSelect
                                    {...field}
                                    options={yearOptions}
                                    placeholder="年"
                                    name="年を選択"
                                    error={errors.year?.message || errors.month?.message || errors.day?.message}
                                    position="first"
                                />
                            )}
                        />

                        <Controller
                            name="month"
                            control={control}
                            render={({ field }) => (
                                <GroupedToggleFormSelect
                                    {...field}
                                    options={monthOptions}
                                    placeholder="月"
                                    name="月を選択"
                                    position="middle"
                                />
                            )}
                        />
                        <Controller
                            name="day"
                            control={control}
                            render={({ field }) => (
                                <GroupedToggleFormSelect
                                    {...field}
                                    options={dayOptions}
                                    placeholder="日"
                                    name="日を選択"
                                    position="last"
                                />
                            )}
                        />
                    </Birthday>
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="アイコン画像" />
                    <ImageUpload
                        imagePath={watch('icon_image') ?? ''}
                        directory="buyer_profile"
                        fileName={'icon'}
                        fileCategory="buyer"
                        disabled={loading}
                        width={180}
                        height={180}
                        onComplete={(fileUrl) => setValue('icon_image', fileUrl)}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="役職" />
                    <Controller
                        name="job"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} value={field.value ?? ''} hasError={!!errors.job?.message} />
                                {errors.job?.message && <FormError message={errors.job.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="部署" />
                    <Controller
                        name="department"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput
                                    {...field}
                                    value={field.value ?? ''}
                                    hasError={!!errors.department?.message}
                                />
                                {errors.department?.message && <FormError message={errors.department.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="権限" isRequired />
                    <Controller
                        name="role"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormSelect
                                    {...field}
                                    options={Object.entries(USER_ROLE_LABEL_FOR_EDIT).map(([key, value]) => ({
                                        value: key,
                                        label: value,
                                    }))}
                                />
                            </div>
                        )}
                    />
                </FlexContainer>
            </FormContainer>
            <ButtonContainer>
                <ActionButton
                    disabled={loading}
                    onClick={submit}
                    bgColor="main-color-blue"
                    style={{ maxWidth: '400px' }}
                >
                    登録する
                </ActionButton>
            </ButtonContainer>
        </Wrapper>
    );
};
